<template>
  <div class="admin-overview">
    <div class="page-header">
      <div class="page-title-group">
        <h2>Visão Geral</h2>
        <p>Acompanhe o desempenho da plataforma em tempo real.</p>
      </div>
      <div class="page-header-actions">
        <select class="form-select w-auto" v-model="selectedPeriod" @change="updatePeriodData">
          <option value="30dias">Últimos 30 dias</option>
          <option value="7dias">Últimos 7 dias</option>
          <option value="hoje">Hoje</option>
          <option value="mes">Este mês</option>
        </select>
        <button class="btn btn-outline-blue"><Download size="16" /> Exportar relatório</button>
      </div>
    </div>

    <!-- 8 KPIs -->
    <div class="kpi-grid-4">
      <div class="kpi-card">
        <div class="kpi-icon-wrapper blue"><Wallet size="20" /></div>
        <div class="kpi-content">
          <div class="kpi-label">Volume transacionado</div>
          <div class="kpi-value">R$ {{ kpis.volume }}</div>
          <div class="kpi-trend positive"><span class="trend-badge"><ArrowUp size="12" /> {{ kpis.volumeTrend }}</span> <span class="trend-text">em relação ao período anterior</span></div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon-wrapper teal"><Receipt size="20" /></div>
        <div class="kpi-content">
          <div class="kpi-label">Receita em taxas</div>
          <div class="kpi-value">R$ {{ kpis.receita }}</div>
          <div class="kpi-trend positive"><span class="trend-badge"><ArrowUp size="12" /> {{ kpis.receitaTrend }}</span> <span class="trend-text">em relação ao período anterior</span></div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon-wrapper blue"><Users size="20" /></div>
        <div class="kpi-content">
          <div class="kpi-label">Clientes ativos</div>
          <div class="kpi-value">{{ kpis.clientes }}</div>
          <div class="kpi-trend positive"><span class="trend-badge"><ArrowUp size="12" /> 12,7%</span> <span class="trend-text">em relação ao período anterior</span></div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon-wrapper amber"><ClipboardList size="20" /></div>
        <div class="kpi-content">
          <div class="kpi-label">Cadastros pendentes</div>
          <div class="kpi-value">{{ kpis.cadastrosPendentes }}</div>
          <div class="kpi-trend warning"><span class="trend-badge"><ArrowUp size="12" /> 26,3%</span> <span class="trend-text">em relação ao período anterior</span></div>
        </div>
      </div>
      
      <div class="kpi-card">
        <div class="kpi-icon-wrapper green-outline"><CheckCircle2 size="20" /></div>
        <div class="kpi-content">
          <div class="kpi-label">Pix aprovados</div>
          <div class="kpi-value">{{ kpis.pixAprovados }}</div>
          <div class="kpi-trend positive"><span class="trend-badge"><ArrowUp size="12" /> 15,8%</span> <span class="trend-text">em relação ao período anterior</span></div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon-wrapper red-outline"><XCircle size="20" /></div>
        <div class="kpi-content">
          <div class="kpi-label">Pix recusados</div>
          <div class="kpi-value">{{ kpis.pixRecusados }}</div>
          <div class="kpi-trend danger"><span class="trend-badge"><ArrowUp size="12" /> 8,4%</span> <span class="trend-text">em relação ao período anterior</span></div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon-wrapper amber-outline"><CreditCard size="20" /></div>
        <div class="kpi-content">
          <div class="kpi-label">Saques pendentes</div>
          <div class="kpi-value">{{ kpis.saquesPendentes }}</div>
          <div class="kpi-trend warning"><span class="trend-badge"><ArrowUp size="12" /> 5,7%</span> <span class="trend-text">em relação ao período anterior</span></div>
        </div>
      </div>
      <div class="kpi-card">
        <div class="kpi-icon-wrapper green-solid"><Wallet size="20" /></div>
        <div class="kpi-content">
          <div class="kpi-label">Saldo total em carteira</div>
          <div class="kpi-value">R$ {{ kpis.saldoTotal }}</div>
          <div class="kpi-trend positive"><span class="trend-badge"><ArrowUp size="12" /> 9,4%</span> <span class="trend-text">em relação ao período anterior</span></div>
        </div>
      </div>
    </div>

    <!-- Charts & Activity -->
    <div class="dashboard-panels">
      <div class="card panel-chart">
        <div class="card-header border-none mb-0">
          <h3 class="card-title">Volume transacionado</h3>
          <select class="form-select w-auto" v-model="chartGranularity" @change="updatePeriodData">
            <option value="diario">Diário</option>
            <option value="semanal">Semanal</option>
            <option value="mensal">Mensal</option>
          </select>
        </div>
        <div class="chart-container">
          <canvas id="adminVolumeChart"></canvas>
        </div>
      </div>

      <div class="card panel-donut">
        <div class="card-header border-none mb-0">
          <h3 class="card-title">Distribuição por tipo de transação</h3>
        </div>
        <div class="donut-content mt-2">
          <div class="donut-wrapper"><canvas id="adminDonutChart"></canvas></div>
          <div class="donut-legend-right">
            <div class="legend-row">
              <div class="legend-label"><span class="dot green"></span> Pix</div>
              <div class="legend-pct">75,6%</div>
              <div class="legend-val">R$ 1.303.712,45</div>
            </div>
            <div class="legend-row">
              <div class="legend-label"><span class="dot orange"></span> Saques</div>
              <div class="legend-pct">12,7%</div>
              <div class="legend-val">R$ 219.008,20</div>
            </div>
            <div class="legend-row">
              <div class="legend-label"><span class="dot red"></span> Transferências</div>
              <div class="legend-pct">7,9%</div>
              <div class="legend-val">R$ 136.420,13</div>
            </div>
            <div class="legend-row">
              <div class="legend-label"><span class="dot purple"></span> Outros</div>
              <div class="legend-pct">3,8%</div>
              <div class="legend-val">R$ 65.420,00</div>
            </div>
            <div class="legend-total-row mt-2">
              <div class="legend-label">Total</div>
              <div class="legend-val font-bold">R$ {{ kpis.volume }}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="card panel-activity">
        <div class="card-header border-none mb-0">
          <h3 class="card-title">Atividades recentes</h3>
          <a href="#" class="link-blue text-sm">Ver todas <ChevronRight size="14" /></a>
        </div>
        <div class="activity-list mt-2">
          <div class="activity-item">
            <div class="activity-icon green"><UserPlus size="14" /></div>
            <div class="activity-info">
              <strong>Novo cliente cadastrado</strong>
              <span>Tech Comércio Ltda</span>
            </div>
            <div class="activity-time">21/05/2025, 10:21</div>
          </div>
          <div class="activity-item">
            <div class="activity-icon green"><CheckCircle2 size="14" /></div>
            <div class="activity-info">
              <strong>Pix recebido</strong>
              <span>R$ 2.950,00 de João da Silva</span>
            </div>
            <div class="activity-time">21/05/2025, 10:18</div>
          </div>
          <div class="activity-item">
            <div class="activity-icon orange"><ArrowUpRight size="14" /></div>
            <div class="activity-info">
              <strong>Saque solicitado</strong>
              <span>R$ 5.200,00 por Maria Oliveira</span>
            </div>
            <div class="activity-time">21/05/2025, 10:16</div>
          </div>
          <div class="activity-item">
            <div class="activity-icon red"><AlertCircle size="14" /></div>
            <div class="activity-info">
              <strong>Webhook falhou</strong>
              <span>pix.recebido — Cliente: Loja Exemplo</span>
            </div>
            <div class="activity-time">21/05/2025, 10:15</div>
          </div>
          <div class="activity-item">
            <div class="activity-icon purple"><ArrowRightLeft size="14" /></div>
            <div class="activity-info">
              <strong>Limite alterado</strong>
              <span>Cliente: Empresa ABC Ltda</span>
            </div>
            <div class="activity-time">21/05/2025, 10:12</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Transações Recentes -->
    <div class="card mt-4">
      <div class="card-header border-none" style="display: flex; justify-content: space-between; align-items: center; padding: 20px;">
        <h3 class="card-title" style="margin: 0;">Transações Recentes</h3>
        <router-link to="/admin/transacoes" class="link-blue text-sm" style="text-decoration: none;">Ver todas <ChevronRight size="14" /></router-link>
      </div>
      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>CLIENTE</th>
              <th>TIPO</th>
              <th>VALOR</th>
              <th>STATUS</th>
              <th>DATA</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tx in recentTransactions" :key="tx.id" class="hoverable">
              <td class="font-mono text-sm font-bold primary-link">{{ tx.id }}</td>
              <td>
                <div class="font-bold text-sm">{{ tx.client }}</div>
                <div class="text-xs text-muted">{{ tx.doc }}</div>
              </td>
              <td><span class="badge badge-info">{{ tx.type }}</span></td>
              <td class="font-bold">R$ {{ tx.amount }}</td>
              <td>
                <span class="badge" :class="tx.statusClass">
                  <span class="dot"></span> {{ tx.status }}
                </span>
              </td>
              <td>{{ tx.date }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { Wallet, Receipt, Users, ClipboardList, CheckCircle2, XCircle, CreditCard, ArrowUp, Download, ChevronRight, UserPlus, ArrowUpRight, AlertCircle, ArrowRightLeft } from 'lucide-vue-next';
import Chart from 'chart.js/auto';

export default {
  name: 'AdminOverviewView',
  components: { Wallet, Receipt, Users, ClipboardList, CheckCircle2, XCircle, CreditCard, ArrowUp, Download, ChevronRight, UserPlus, ArrowUpRight, AlertCircle, ArrowRightLeft },
  data() {
    return {
      selectedPeriod: '30dias',
      chartGranularity: 'diario',
      volumeChart: null,
      donutChart: null,
      kpis: {
        volume: '1.724.560,78',
        volumeTrend: '18,6%',
        receita: '45.680,32',
        receitaTrend: '16,3%',
        clientes: '1.842',
        cadastrosPendentes: '38',
        pixAprovados: '18.672',
        pixRecusados: '246',
        saquesPendentes: '112',
        saldoTotal: '1.248.932,45'
      },
      recentTransactions: [
        { id: 'PIX-8f3d2e7a1c', client: 'Tech Comércio Ltda', doc: '12.345.678/0001-90', type: 'Pix', amount: '100,00', status: 'Aprovado', statusClass: 'badge-success-light', date: '21/05/2025, 10:23' },
        { id: 'PIX-a1b2c3d4e5', client: 'Loja Exemplo SA', doc: '98.765.432/0001-10', type: 'Pix', amount: '2.500,00', status: 'Aprovado', statusClass: 'badge-success-light', date: '21/05/2025, 09:47' },
        { id: 'SAQ-9918237190', client: 'Tech Comércio Ltda', doc: '12.345.678/0001-90', type: 'Saque', amount: '5.000,00', status: 'Pendente', statusClass: 'badge-warning-light', date: '21/05/2025, 10:16' },
        { id: 'FLH-9928381923', client: 'Serviços ABC Eireli', doc: '11.222.333/0001-44', type: 'Pix', amount: '99,90', status: 'Recusado', statusClass: 'badge-danger-light', date: '21/05/2025, 10:15' }
      ]
    }
  },
  mounted() {
    this.initCharts();
  },
  beforeUnmount() {
    if (this.volumeChart) this.volumeChart.destroy();
    if (this.donutChart) this.donutChart.destroy();
  },
  methods: {
    updatePeriodData() {
      if (this.selectedPeriod === '7dias') {
        this.kpis.volume = '412.300,50';
        this.kpis.receita = '11.240,10';
        this.kpis.pixAprovados = '4.520';
        this.kpis.pixRecusados = '58';
      } else if (this.selectedPeriod === 'hoje') {
        this.kpis.volume = '68.950,00';
        this.kpis.receita = '1.820,40';
        this.kpis.pixAprovados = '812';
        this.kpis.pixRecusados = '12';
      } else if (this.selectedPeriod === 'mes') {
        this.kpis.volume = '1.280.400,00';
        this.kpis.receita = '34.500,80';
        this.kpis.pixAprovados = '14.120';
        this.kpis.pixRecusados = '180';
      } else {
        this.kpis.volume = '1.724.560,78';
        this.kpis.receita = '45.680,32';
        this.kpis.pixAprovados = '18.672';
        this.kpis.pixRecusados = '246';
      }

      if (this.volumeChart) {
        if (this.selectedPeriod === '7dias') {
          this.volumeChart.data.labels = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
          this.volumeChart.data.datasets[0].data = [40, 65, 50, 90, 75, 110, 130];
        } else if (this.selectedPeriod === 'hoje') {
          this.volumeChart.data.labels = ['08h', '10h', '12h', '14h', '16h', '18h'];
          this.volumeChart.data.datasets[0].data = [10, 25, 40, 55, 30, 70];
        } else {
          this.volumeChart.data.labels = ['21/04', '26/04', '01/05', '06/05', '11/05', '16/05', '21/05'];
          this.volumeChart.data.datasets[0].data = [20, 45, 25, 60, 40, 85, 110];
        }
        this.volumeChart.update();
      }
    },
    initCharts() {
      // Line chart
      const volCtx = document.getElementById('adminVolumeChart').getContext('2d');
      this.volumeChart = new Chart(volCtx, {
        type: 'line',
        data: {
          labels: ['21/04', '26/04', '01/05', '06/05', '11/05', '16/05', '21/05'],
          datasets: [{
            data: [20, 45, 25, 60, 40, 85, 110],
            borderColor: '#01A95A', backgroundColor: 'rgba(1, 169, 90, 0.1)', borderWidth: 2, fill: true, tension: 0.4,
            pointBackgroundColor: '#fff', pointBorderColor: '#01A95A', pointRadius: 4
          }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } },
          scales: {
            y: { grid: { borderDash: [4, 4] }, ticks: { callback: v => 'R$ '+v+'k', font: { size: 10 } } },
            x: { grid: { display: false }, ticks: { font: { size: 10 } } }
          }
        }
      });

      // Donut chart
      const donutCtx = document.getElementById('adminDonutChart').getContext('2d');
      this.donutChart = new Chart(donutCtx, {
        type: 'doughnut',
        data: {
          labels: ['Pix', 'Saques', 'Transferências', 'Outros'],
          datasets: [{
            data: [75.6, 12.7, 7.9, 3.8],
            backgroundColor: ['#01A95A', '#F59E0B', '#EF4444', '#8B5CF6'],
            borderWidth: 0, cutout: '75%'
          }]
        },
        options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } }
      });
    }
  }
}
</script>

<style scoped>
.page-header { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 24px; }
.page-title-group h2 { font-size: 1.75rem; font-weight: 700; color: var(--text-primary); margin-bottom: 4px; }
.page-title-group p { font-size: 0.9rem; color: var(--text-secondary); margin: 0; }
.page-header-actions { display: flex; gap: 12px; }

.btn-outline-blue { display: inline-flex; align-items: center; gap: 8px; padding: 10px 16px; border: 1px solid var(--accent-blue-light); border-radius: var(--radius-md); color: var(--accent-blue); background: white; font-weight: 600; font-size: 0.85rem; cursor: pointer; border-color: #BFDBFE; }
.form-select { padding: 10px 14px; border: 1px solid var(--border-color); border-radius: var(--radius-md); font-size: 0.85rem; outline: none; background: white; cursor: pointer; }
.w-auto { width: auto; }

.kpi-grid-4 {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 1600px) {
  .kpi-grid-4 {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .kpi-grid-4 {
    grid-template-columns: 1fr;
  }
}

.kpi-card { background: white; border: 1px solid var(--border-color); border-radius: var(--radius-lg); padding: 18px; display: flex; gap: 14px; box-shadow: var(--shadow-sm); }
.kpi-icon-wrapper { width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.kpi-icon-wrapper.blue { background: var(--accent-blue-light); color: var(--accent-blue); }
.kpi-icon-wrapper.teal { background: #F0FDF4; color: #14B8A6; }
.kpi-icon-wrapper.amber { background: #FFFBEB; color: #D97706; }
.kpi-icon-wrapper.green-outline { background: white; border: 1px solid #01A95A; color: #01A95A; }
.kpi-icon-wrapper.red-outline { background: white; border: 1px solid #EF4444; color: #EF4444; }
.kpi-icon-wrapper.amber-outline { background: white; border: 1px solid #F59E0B; color: #F59E0B; }
.kpi-icon-wrapper.green-solid { background: #01A95A; color: white; }

.kpi-content { flex: 1; display: flex; flex-direction: column; justify-content: center; min-width: 0; }
.kpi-label { font-size: 0.85rem; color: var(--text-secondary); font-weight: 600; margin-bottom: 4px; }
.kpi-value { font-size: 1.25rem; font-weight: 700; color: var(--text-primary); margin-bottom: 4px; white-space: nowrap; }
.kpi-trend { font-size: 0.75rem; font-weight: 700; display: flex; align-items: center; flex-wrap: wrap; gap: 2px 6px; text-align: left; }
.kpi-trend.positive { color: var(--accent-green); }
.kpi-trend.warning { color: var(--accent-amber); }
.kpi-trend.danger { color: var(--accent-red); }
.trend-badge { display: inline-flex; align-items: center; gap: 3px; white-space: nowrap; flex-shrink: 0; }
.trend-text { color: var(--text-muted); font-weight: 500; text-align: left; line-height: 1.25; white-space: nowrap; }

.dashboard-panels {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 24px;
}

.dashboard-panels > * {
  min-width: 0;
}

.panel-chart {
  grid-column: span 2;
}

.panel-donut {
  grid-column: span 1;
}

.panel-activity {
  grid-column: span 1;
}

@media (max-width: 900px) {
  .dashboard-panels {
    grid-template-columns: 1fr;
  }
  .panel-chart, .panel-donut, .panel-activity {
    grid-column: span 1;
  }
}

.card { background: white; border: 1px solid var(--border-color); border-radius: var(--radius-lg); box-shadow: var(--shadow-sm); padding: 20px; min-width: 0; }
.card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.border-none { border-bottom: none; }
.mb-0 { margin-bottom: 0; }
.card-title { font-size: 1.05rem; font-weight: 700; color: var(--text-primary); }

.chart-container {
  height: 220px;
  width: 100%;
  margin-top: 16px;
  position: relative;
}

@media (max-width: 768px) {
  .chart-container {
    height: 180px;
    margin-top: 12px;
  }
}

@media (max-width: 480px) {
  .chart-container {
    height: 160px;
  }
}

.donut-content {
  display: flex;
  align-items: center;
  gap: 24px;
  margin-top: 12px;
  min-width: 0;
}

.donut-wrapper {
  width: 150px;
  height: 150px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.donut-legend-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.legend-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.82rem;
}

.legend-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.dot.green { background: #01A95A; }
.dot.orange { background: #F59E0B; }
.dot.red { background: #EF4444; }
.dot.purple { background: #8B5CF6; }

.legend-pct {
  color: var(--text-muted);
  font-size: 0.78rem;
  white-space: nowrap;
}

.legend-val {
  color: var(--text-primary);
  font-weight: 600;
  text-align: right;
  white-space: nowrap;
}

.legend-total-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid var(--border-color);
  padding-top: 10px;
  margin-top: 4px;
}

.legend-total-row .legend-label {
  font-weight: 700;
  color: var(--text-primary);
}

.legend-total-row .legend-val {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-primary);
}

@media (max-width: 600px) {
  .donut-content {
    flex-direction: column;
  }
  .donut-wrapper {
    width: 140px;
    height: 140px;
  }
}
.font-bold { font-weight: 700; }

.activity-list { display: flex; flex-direction: column; gap: 16px; }
.activity-item { display: flex; gap: 12px; align-items: flex-start; }
.activity-icon { width: 28px; height: 28px; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; margin-top: 2px; }
.activity-icon.green { background: #E6F7EF; color: #01A95A; }
.activity-icon.orange { background: #FFFBEB; color: #D97706; }
.activity-icon.red { background: #FEF2F2; color: #EF4444; }
.activity-icon.purple { background: #F3E8FF; color: #8B5CF6; }
.activity-info { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.activity-info strong { font-size: 0.8rem; color: var(--text-primary); }
.activity-info span { font-size: 0.7rem; color: var(--text-secondary); line-height: 1.3; }
.activity-time { font-size: 0.7rem; color: var(--text-muted); white-space: nowrap; }

.link-blue { display: inline-flex; align-items: center; gap: 4px; color: var(--accent-blue); text-decoration: none; font-size: 0.85rem; font-weight: 600; }
.mt-2 { margin-top: 8px; }
.mt-4 { margin-top: 24px; }
</style>
